<template>
  <div class="app-menu-container" :offline="isOfflineModeEnabled">
    <v-navigation-drawer
      class="menu-container surface"
      width="375"
      :mini-variant="mini || $route.name === 'KPISubscriptions'"
      mini-variant-width="59"
      permanent
      absolute
    >
      <v-row id="sidebar-menu-container" no-gutters @mouseenter.stop="onMouseOver">
        <!-- Menu Section -->
        <v-col id="icons-column" align-self="start">
          <!-- Application Menu Items -->
          <div
            v-for="item in applicationMenuItems"
            :key="item.name"
            class="item-container"
            @click.stop="handleMenuClick(item)"
          >
            <router-link v-slot="{ navigate, isActive }" custom :to="{ name: item.name }">
              <div
                :id="item.id"
                class="menu-item application-menu-items"
                :class="{
                  'item-selected': isActive,
                  'surface lighten-1': isActive
                }"
                :dark="$vuetify.theme.dark"
                :aria-label="item.name"
                @click="navigate"
              >
                <div v-show="item.notifications" class="badge">
                  {{ item.notifications }}
                </div>
                <div>
                  <svg
                    :id="'svg_' + item.name"
                    :class="{
                      active: isActive,
                      'pulsate-bck': isActive
                    }"
                    viewBox="0 0 24 24"
                    width="40px"
                    height="40px"
                  >
                    <!-- Icon rendered here after mount() -->
                  </svg>
                </div>
              </div>
            </router-link>
          </div>
          <div :style="spacerHeight"></div>
          <!-- System Menu Items -->
          <div
            v-for="item in systemMenuItems"
            :key="item.name"
            class="item-container"
            @click.stop="handleMenuClick(item)"
          >
            <router-link v-slot="{ navigate, isActive }" custom :to="{ name: item.name }">
              <div
                :id="item.id"
                class="menu-item system-menu-items item-container"
                :class="{
                  'item-selected': isActive,
                  'surface lighten-1': isActive
                }"
                :dark="$vuetify.theme.dark"
                :aria-label="item.name"
                @click="navigate"
              >
                <div>
                  <svg
                    :id="'svg_' + item.name"
                    :class="{
                      active: isActive,
                      'pulsate-bck': isActive
                    }"
                    viewBox="0 0 24 24"
                    width="40px"
                    height="40px"
                  >
                    <!-- Icon rendered here after mount() -->
                  </svg>
                </div>
              </div>
            </router-link>
          </div>

          <v-row no-gutters>
            <!-- Spacer for 1 button height -->
            <div style="height: 64px">&nbsp;</div>
          </v-row>
        </v-col>
        <v-col v-show="!mini">
          <!-- Application Side Bar -->
          <EcsAppSideBar :border-radius="addBorderRadius" :selected-menu="selectedMenu" />
        </v-col>
        <div class="pin-side-bar-container">
          <v-fab-transition>
            <v-btn
              v-show="!mini"
              class="pin-side-bar-btn"
              color="primary"
              x-small
              fab
              depressed
              @click="isMiniModeEnabled ? onClickToDisableMiniMode() : onClickToEnableMiniMode()"
            >
              <v-icon>{{ miniModeIcon }}</v-icon>
            </v-btn>
          </v-fab-transition>
        </div>
      </v-row>
    </v-navigation-drawer>
    <div
      ref="appMenuOverlay"
      class="app-menu-overlay"
      @click.stop="collapseMenu"
      @mouseenter.stop="onMouseOverOverlay"
    ></div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsAppSideBar from '@/components/ecs-app-side-bar/EcsAppSideBar';
import iconSet from '@/components/ecs-app-menu/resources/icons';

export default {
  name: 'AppMenu',

  components: {
    EcsAppSideBar
  },

  data() {
    return {
      addBorderRadius: false,
      mini: true,
      isMiniModeEnabled: true,
      selectedMenu: {}
    };
  },

  computed: {
    ...mapGetters('AGENT', ['isSupervisor']),

    ...mapGetters('NOTIFICATIONS', [
      'getAllNotifications',
      'getTotalChatInteractionsWithNotifications',
      'getTotalSmsInteractionsWithNotifications'
    ]),

    ...mapGetters('SETTINGS', ['featuresSettings', 'isOfflineModeEnabled']),

    applicationMenuItems() {
      const result = [];

      result.push(
        {
          id: 'side-bar-dashboards',
          name: 'Dashboards',
          icon: iconSet.dashboardIcon,
          notifications: 0,
          action: this.clearFocusedInteraction
        },
        {
          id: 'side-bar-calls',
          name: 'Calls',
          icon: iconSet.phoneIcon,
          notificationKey: 'CALLS',
          notifications: 0,
          action: () => ({})
        },
        {
          id: 'side-bar-chats',
          name: 'Chats',
          icon: iconSet.chatIcon,
          notificationKey: 'CHATS',
          notifications: this.getTotalChatInteractionsWithNotifications,
          action: () => ({})
        },
        {
          id: 'side-bar-emails',
          name: 'Emails',
          icon: iconSet.emailIcon,
          notificationKey: 'MAILS',
          notifications: 0,
          action: () => ({})
        },
        {
          id: 'side-bar-sms',
          name: 'Sms',
          icon: iconSet.smsIcon,
          notificationKey: 'SMS',
          notifications: this.getTotalSmsInteractionsWithNotifications,
          action: () => ({})
        }
      );
      if (this.isSupervisor) {
        result.push({
          id: 'side-bar-supervisor',
          name: 'Supervisor',
          icon: iconSet.supervisorIcon,
          notificationKey: 'Supervisor',
          notifications: 0,
          action: () => ({})
        });
      }

      return result;
    },

    systemMenuItems() {
      return [
        {
          id: 'side-bar-settings',
          name: 'Settings',
          icon: iconSet.settingsIcon,
          action: this.clearFocusedInteraction
        },
        {
          id: 'side-bar-help',
          name: 'Help',
          icon: iconSet.helpIcon,
          action: this.clearFocusedInteraction
        }
      ];
    },

    menuItems() {
      return [...this.applicationMenuItems, ...this.systemMenuItems];
    },

    spacerHeight() {
      return `height: calc(100% - ${64 * (this.applicationMenuItems.length + this.systemMenuItems.length + 1)}px)`;
    },

    miniModeIcon() {
      return this.isMiniModeEnabled ? 'mdi-pin-off' : 'mdi-pin';
    }
  },

  watch: {
    $route(to) {
      const menuItem = this.getMenuByRouteName(to.name);
      if (menuItem) {
        this.setSelectedMenu(menuItem.item, menuItem.index === 0);
      }
    },

    getTotalChatInteractionsWithNotifications(value) {
      this.$root.$emit('change-favicon');
      this.applicationMenuItems[2].notifications = value;
    },

    'featuresSettings.pinnedSideBarOnMaximizedMode'(isPinned) {
      if (isPinned) {
        this.disableMiniMode();
      } else {
        this.enableMiniMode();
      }
    }
  },

  mounted() {
    if (this.$route.name) {
      const menuItem = this.getMenuByRouteName(this.$route.name);
      if (menuItem) {
        this.setSelectedMenu(menuItem.item, menuItem.index === 0);
      }
    }

    if (this.featuresSettings.pinnedSideBarOnMaximizedMode) {
      this.disableMiniMode();
    }

    this.$nextTick(function() {
      this.applicationMenuItems.forEach(item => this.generateIconData(item));
      this.systemMenuItems.forEach(item => this.generateIconData(item));
    });

    this.$root.$on('disable-mini-mode-tour', this.onClickToDisableMiniMode);
    this.$root.$on('enable-mini-mode-tour', this.onClickToEnableMiniMode);
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.$root.$off('disable-mini-mode-tour', this.onClickToDisableMiniMode);
    this.$root.$off('enable-mini-mode-tour', this.onClickToEnableMiniMode);
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    ...mapActions('SETTINGS', ['updatePinnedSideBarOnMaximizedMode']),

    ...mapActions('WINDOWS', ['clearFocusedInteraction']),

    generateIconData(element) {
      if (element && element.name && element.icon) {
        iconSet.render(element.icon, document.getElementById(`svg_${element.name}`));
      } else {
        console.warn('DEV: Unable to render element icon, please check data.');
      }
    },

    getMenuByRouteName(routeName) {
      const menuItemIndex = this.menuItems.findIndex(item => item.name === routeName);
      return menuItemIndex === -1 ? null : { item: this.menuItems[menuItemIndex], index: menuItemIndex };
    },

    handleMenuClick(item) {
      if (this.isMiniModeEnabled) {
        this.mini = false;
        this.$refs.appMenuOverlay.style.display = 'block';
      }
      item.action();
    },

    onClickToDisableMiniMode() {
      this.updatePinnedSideBarOnMaximizedMode(true);
    },

    onClickToEnableMiniMode() {
      this.updatePinnedSideBarOnMaximizedMode(false);
    },

    disableMiniMode() {
      this.isMiniModeEnabled = false;
      this.mini = false;
      this.$refs.appMenuOverlay.style.display = 'none';
      this.$emit('mini-mode-disabled', true);
    },

    enableMiniMode() {
      this.collapseMenu();
      this.$emit('mini-mode-disabled', false);
    },

    collapseMenu() {
      if (!this.isMiniModeEnabled) {
        this.mini = true;
        this.isMiniModeEnabled = true;
        this.$refs.appMenuOverlay.style.display = 'none';
      }
    },

    setSelectedMenu(menuItem, isFirst) {
      this.selectedMenu = menuItem;
      this.addBorderRadius = !isFirst;
    },

    onMouseOver() {
      if (this.isMiniModeEnabled && this.mini) {
        this.mini = false;
        this.$refs.appMenuOverlay.style.display = 'block';
      }
    },

    onMouseOverOverlay() {
      if (this.isMiniModeEnabled) {
        this.mini = true;
        this.$refs.appMenuOverlay.style.display = 'none';
      }
    },

    handleResize() {
      if (this.featuresSettings.pinnedSideBarOnMaximizedMode) {
        this.disableMiniMode();
      }
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}
#icons-column {
  max-width: 60px;
  height: 100%;
}
#sidebar-menu-container {
  height: 100%;
  overflow: hidden;
}
.app-menu-container {
  position: absolute;
  top: 64px;
  height: calc(100% - 64px) !important;
  width: 100%;
}
[offline].app-menu-container {
  top: 94px !important;
  height: calc(100% - 94px) !important;
}
.app-menu-overlay {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 119;
  background: transparent;
}
.menu-container {
  z-index: 150;
  overflow: visible;
}

.menu-list {
  padding-bottom: 0;
}

.menu-item {
  padding: 8px;
  position: relative;
  transition: background-color 0.3s ease;
}

.menu-item:hover {
  background-color: rgba(0, 0, 0, 0.07);
}

.item-container:first-child {
  margin-top: -1px;
}

.item-container {
  border: 1px solid transparent;
  border-right: none;
  right: -2px;
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
  margin-bottom: -2px;
}

.app-menu-container .item-container .menu-item {
  border: 1px solid transparent;
  border-radius: 12px 0 0 12px;
  transition: all 0.2s ease;
}

.app-menu-container .item-container .menu-item.item-selected[dark] {
  border: 1px solid #0005 !important;
}

.app-menu-container .item-container .menu-item.item-selected {
  border: 1px solid var(--v-border-color) !important;
  border-right: none !important;
}

.badge {
  height: 18px;
  line-height: 18px;
  background-color: var(--v-primary-base);
  color: #fff;
  font-size: 12px;
  text-align: center;
  padding: 0 7px;
  border-radius: 10px;
  position: absolute;
  z-index: 1;
  top: 0;
  right: 2px;
}

.pin-side-bar-container {
  width: 0;
}

.pin-side-bar-btn {
  z-index: 121;
  top: 56px;
  right: -8px;
  width: 24px;
  height: 24px;
  position: absolute;
}
</style>
